/**
 * Zustand store cho game state Phase 1.
 *
 * Actions wrap backend API:
 *   - register / login
 *   - loadShop
 *   - startSession
 *   - serveOrder
 *   - endSession
 *
 * KHÔNG dùng optimistic update — chờ response server (backend authoritative).
 *
 * Tham chiếu: `docs/analysis/01_system_design_analysis.md` mục 3.2 + 4.2.
 */
import { create } from "zustand";

import { api, setAccessToken } from "../lib/api";
import { newActionId } from "../lib/idempotency";
import type {
  FloatingCoin,
  Milestone,
  Order,
  RevenueBlock,
  ShopState,
} from "../types";

interface SessionInfo {
  session_id: string;
  shop_id: string;
  status: "active" | "ended";
  started_at: string;
  total_orders_served: number;
  total_bean_earned: number;
}

interface ServeResult {
  is_correct: boolean;
  timed_out: boolean;
  bean_earned: number;
  tip_earned: number;
  reputation_earned: number;
  milestones_unlocked: Milestone[];
  hint: string | null;
}

interface EndResult {
  total_orders: number;
  total_correct: number;
  total_wrong: number;
  total_abandoned: number;
  total_bean_earned: number;
  duration_seconds: number;
}

interface ShopStoreState {
  shop: ShopState | null;
  session: SessionInfo | null;
  currentOrder: Order | null;
  revenue: RevenueBlock | null;
  floatingCoins: FloatingCoin[];
  isLoading: boolean;
  lastError: string | null;

  register(email: string, password: string): Promise<void>;
  login(email: string, password: string): Promise<void>;
  telegramAuth(initData: string): Promise<{ isNewUser: boolean }>;
  logout(): void;
  loadShop(): Promise<void>;
  setShopName(name: string): Promise<void>;
  loadActiveSession(): Promise<void>;
  startSession(): Promise<void>;
  serveOrder(ingredients: string[]): Promise<ServeResult>;
  endSession(): Promise<EndResult>;
  pushFloatingCoin(coin: FloatingCoin): void;
  removeFloatingCoin(id: string): void;
}

interface Envelope<T> {
  data: T;
}

const EMPTY_REVENUE: RevenueBlock = {
  session_revenue: 0,
  today_revenue: 0,
  shop_balance: 0,
  today_revenue_delta_pct: null,
  today_orders: 0,
};

export const useShopStore = create<ShopStoreState>((set, get) => ({
  shop: null,
  session: null,
  currentOrder: null,
  revenue: null,
  floatingCoins: [],
  isLoading: false,
  lastError: null,

  async register(email, password) {
    set({ isLoading: true, lastError: null });
    try {
      const res = await api.post<
        Envelope<{ access_token: string; shop: ShopState }>
      >("/auth/register", {
        email,
        password,
        client_action_id: newActionId(),
      });
      setAccessToken(res.data.data.access_token);
      set({
        shop: res.data.data.shop,
        revenue: EMPTY_REVENUE,
        isLoading: false,
      });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  async login(email, password) {
    set({ isLoading: true, lastError: null });
    try {
      const res = await api.post<Envelope<{ access_token: string }>>(
        "/auth/login",
        {
          email,
          password,
          client_action_id: newActionId(),
        },
      );
      setAccessToken(res.data.data.access_token);
      set({ isLoading: false });
      await get().loadShop();
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  async telegramAuth(initData: string) {
    set({ isLoading: true, lastError: null });
    try {
      const res = await api.post<
        Envelope<{
          access_token: string;
          is_new_user: boolean;
          shop: ShopState;
        }>
      >("/auth/telegram", {
        init_data: initData,
        client_action_id: newActionId(),
      });
      setAccessToken(res.data.data.access_token);
      set({
        shop: res.data.data.shop,
        revenue: EMPTY_REVENUE,
        isLoading: false,
      });
      return { isNewUser: res.data.data.is_new_user };
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  logout() {
    setAccessToken(null);
    set({
      shop: null,
      session: null,
      currentOrder: null,
      revenue: null,
      floatingCoins: [],
      lastError: null,
    });
  },

  async loadShop() {
    const res = await api.get<Envelope<{ shop: ShopState }>>("/shop");
    set({ shop: res.data.data.shop });
  },

  async setShopName(name) {
    const res = await api.post<Envelope<{ shop: ShopState }>>("/shop", {
      name,
      client_action_id: newActionId(),
    });
    set({ shop: res.data.data.shop });
  },

  async loadActiveSession() {
    const res = await api.get<
      Envelope<{
        session: SessionInfo | null;
        current_order: Order | null;
        shop_state: ShopState;
        revenue: RevenueBlock;
      }>
    >("/sessions/active");
    const d = res.data.data;
    set({
      session: d.session,
      currentOrder: d.current_order,
      shop: d.shop_state,
      revenue: d.revenue,
    });
  },

  async startSession() {
    const res = await api.post<
      Envelope<{
        session: SessionInfo;
        current_order: Order;
        shop_state: ShopState;
        revenue: RevenueBlock;
      }>
    >("/sessions/start", { client_action_id: newActionId() });
    const d = res.data.data;
    set({
      session: d.session,
      currentOrder: d.current_order,
      shop: d.shop_state,
      revenue: d.revenue,
    });
  },

  async serveOrder(ingredients) {
    const { session, currentOrder } = get();
    if (!session || !currentOrder) {
      throw new Error("Không có session/order đang chờ");
    }
    const res = await api.post<
      Envelope<{
        order_id: string;
        is_correct: boolean;
        timed_out: boolean;
        bean_earned: number;
        tip_earned: number;
        reputation_earned: number;
        is_rush_hour: boolean;
        shop_state: ShopState;
        revenue: RevenueBlock;
        next_order: Order | null;
        milestones_unlocked: Milestone[];
        hint: string | null;
      }>
    >(
      `/sessions/${session.session_id}/orders/${currentOrder.order_id}/serve`,
      {
        client_action_id: newActionId(),
        ingredients,
      },
    );
    const d = res.data.data;
    set({
      shop: d.shop_state,
      revenue: d.revenue,
      currentOrder: d.next_order,
    });
    return {
      is_correct: d.is_correct,
      timed_out: d.timed_out,
      bean_earned: d.bean_earned,
      tip_earned: d.tip_earned,
      reputation_earned: d.reputation_earned,
      milestones_unlocked: d.milestones_unlocked,
      hint: d.hint,
    };
  },

  async endSession() {
    const { session } = get();
    if (!session) throw new Error("Không có session đang mở");
    const res = await api.post<
      Envelope<{
        session: SessionInfo;
        summary: EndResult;
      }>
    >(`/sessions/${session.session_id}/end`, {
      client_action_id: newActionId(),
    });
    set({ session: null, currentOrder: null });
    await get().loadShop();
    return res.data.data.summary;
  },

  pushFloatingCoin(coin) {
    set((s) => ({ floatingCoins: [...s.floatingCoins, coin] }));
  },

  removeFloatingCoin(id) {
    set((s) => ({
      floatingCoins: s.floatingCoins.filter((c) => c.id !== id),
    }));
  },
}));
